import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useErrorToast } from "@/hooks/useErrorToast";
import {
  type PortfolioMatrixJob,
  portfolioMatrixQueryKey,
} from "@/hooks/queries/portfolio/useGetPortfolioMatrix";
import { guacHttpClient } from "@/lib/http";
import {
  HttpResponse,
  JobCategory,
  JobInternalConfig,
  JobsResponse,
  UpdateJobInternalConfigRequest,
} from "@/types";

interface UpdateJobCategoryVariables {
  companyId: string;
  jobId: string;
  internalConfigId: string;
  category: JobCategory;
}

/**
 * Sets a job's category in place from the Portfolio matrix. The matrix row
 * carries the internal-config id, so this is a single PATCH onto that config.
 * The matrix row is patched optimistically and rolled back on error.
 */
export const useUpdateSetPortfolioJobCategory = () => {
  const queryClient = useQueryClient();
  const errorToast = useErrorToast();

  return useMutation({
    mutationFn: async ({
      companyId,
      jobId,
      internalConfigId,
      category,
    }: UpdateJobCategoryVariables) => {
      const path = `companies/${companyId}/jobs/${jobId}/job-internal-configs/${internalConfigId}`;
      const res = await guacHttpClient.patch<
        JobInternalConfig,
        HttpResponse<JobInternalConfig>,
        UpdateJobInternalConfigRequest
      >(path, { category });

      return res.data;
    },
    onMutate: async ({ jobId, category }) => {
      await queryClient.cancelQueries({ queryKey: portfolioMatrixQueryKey });
      const previousMatrix =
        queryClient.getQueryData<PortfolioMatrixJob[]>(portfolioMatrixQueryKey);

      queryClient.setQueryData<PortfolioMatrixJob[]>(
        portfolioMatrixQueryKey,
        (jobs) =>
          jobs?.map((job) =>
            job.id === jobId ? { ...job, category } : job,
          ),
      );

      return { previousMatrix };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousMatrix) {
        queryClient.setQueryData(
          portfolioMatrixQueryKey,
          context.previousMatrix,
        );
      }
      errorToast({ description: "Couldn't update the job category." });
    },
    onSuccess: (updatedConfig, { companyId, jobId }) => {
      // Keep the internal-config tab and the jobs list in sync with the change.
      queryClient.setQueryData(
        ["job-internal-configs", companyId, jobId],
        updatedConfig,
      );
      queryClient.setQueryData<JobsResponse[]>(["jobs", companyId], (cached) =>
        cached?.map((jr) =>
          jr.job.id === jobId
            ? { ...jr, jobInternalConfig: updatedConfig }
            : jr,
        ),
      );
    },
  });
};
